import { type RefObject, useCallback } from 'react';

import { EllipsisVerticalIcon } from '@air/design-icons';
import { Button, type ButtonVariants } from '@air/primitive-button';
import { DropdownMenu, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { Spinner } from '@air/primitive-spinner';
import { cn } from '@air/utils-tailwind';

import { COPY_URL_BUTTON, COPY_URL_FIELD_TEXT } from '~/constants/testIDs';
import { useCopyLinkToClipboard } from '~/hooks/useCopyLinkToClipboard';

export type MenuAction = {
  action: () => void;
  text: string;
};

export type CopyFieldProps = {
  copyButtonRef?: RefObject<HTMLButtonElement>;
  hideCopyButton?: boolean;
  url: string | undefined;
  onCopy?: (url: string) => void;
  menuActions?: MenuAction[];
  isDisabled?: boolean;
  buttonColor?: ButtonVariants['color'];
  className?: string;
};

export const CopyField = ({
  buttonColor = 'blue',
  copyButtonRef,
  hideCopyButton = false,
  url,
  menuActions = [],
  onCopy,
  isDisabled,
  className,
}: CopyFieldProps) => {
  const { copyLinkToClipboard, copied } = useCopyLinkToClipboard();

  const copyLink = useCallback(() => {
    if (!url || isDisabled) {
      return;
    }
    // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
    copyLinkToClipboard(url, onCopy);
  }, [copyLinkToClipboard, url, isDisabled, onCopy]);

  return (
    <div
      className={cn(
        'flex h-12 items-center justify-between rounded-4 border ads:border-input border-gray-5',
        className,
      )}
    >
      {!url && !isDisabled && (
        <div className="ml-2">
          <Spinner className="size-5 ads:text-ads-cyan-10 text-teal-9" />
        </div>
      )}
      <div className="ml-3 flex min-w-0 items-center">
        <span className="truncate ads:text-secondary text-gray-11" data-testid={COPY_URL_FIELD_TEXT}>
          {url}
        </span>
      </div>
      {/* Need to unset minWidth declared in Box component's BoxProps or share links affect button width */}
      <div className="flex items-center gap-2 pr-2" style={{ minWidth: 'unset' }}>
        {!hideCopyButton && (
          <Button
            className="ml-0.5"
            appearance="filled"
            color={buttonColor}
            onClick={copyLink}
            ref={copyButtonRef}
            size="medium"
            disabled={isDisabled}
            // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
            data-testid={isDisabled ? `${COPY_URL_BUTTON}-disabled` : COPY_URL_BUTTON}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
        {menuActions.length > 0 && (
          <DropdownMenu
            trigger={
              <IconButton
                adsIcon
                label="Open menu"
                icon={EllipsisVerticalIcon}
                size="medium"
                appearance="filled"
                color="gray"
              />
            }
          >
            {renderDropdownItems({
              options: menuActions.map(({ text: label, action: onSelect }) => ({
                id: label,
                label,
                onSelect,
                type: 'item',
              })),
            })}
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
